import { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    discount_percent: '',
    max_discount: '',
    min_order: '',
    expiry_date: '',
    usage_limit: ''
  });

  const fetchCoupons = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/coupons`, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCoupons(data);
      }
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        code: formData.code,
        discount_percent: parseFloat(formData.discount_percent),
        max_discount: formData.max_discount ? parseFloat(formData.max_discount) : null,
        min_order: formData.min_order ? parseFloat(formData.min_order) : null,
        expiry_date: formData.expiry_date,
        usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null
      };

      const response = await fetch(`${API}/coupons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create coupon');
      }

      toast.success('Coupon created');
      await fetchCoupons();
      setShowForm(false);
      setFormData({
        code: '',
        discount_percent: '',
        max_discount: '',
        min_order: '',
        expiry_date: '',
        usage_limit: ''
      });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (couponId) => {
    if (!confirm('Are you sure you want to delete this coupon?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/coupons/${couponId}`, {
        method: 'DELETE',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete coupon');

      toast.success('Coupon deleted');
      await fetchCoupons();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const formatPrice = (price) => {
    if (!price) return '-';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const isExpired = (date) => {
    return new Date(date) < new Date();
  };

  return (
    <div className="space-y-6" data-testid="admin-coupons">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h1 className="font-heading text-2xl md:text-3xl text-secondary">Coupons</h1>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="rounded-full bg-primary"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Coupon
        </Button>
      </div>

      {/* Create Coupon Form */}
      {showForm && (
        <div className="bg-white rounded-2xl p-6 shadow-soft">
          <h2 className="font-heading text-lg text-secondary mb-4">Create New Coupon</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Coupon Code</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="e.g., SAVE20"
                  required
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label>Discount (%)</Label>
                <Input
                  type="number"
                  value={formData.discount_percent}
                  onChange={(e) => setFormData({ ...formData, discount_percent: e.target.value })}
                  placeholder="e.g., 20"
                  required
                  min="1"
                  max="100"
                  className="mt-1 rounded-xl"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Max Discount (₹) - Optional</Label>
                <Input
                  type="number"
                  value={formData.max_discount}
                  onChange={(e) => setFormData({ ...formData, max_discount: e.target.value })}
                  placeholder="e.g., 500"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label>Min Order Value (₹) - Optional</Label>
                <Input
                  type="number"
                  value={formData.min_order}
                  onChange={(e) => setFormData({ ...formData, min_order: e.target.value })}
                  placeholder="e.g., 1000"
                  className="mt-1 rounded-xl"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                  required
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label>Usage Limit - Optional</Label>
                <Input
                  type="number"
                  value={formData.usage_limit}
                  onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value })}
                  placeholder="e.g., 100"
                  className="mt-1 rounded-xl"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <Button type="submit" disabled={loading} className="rounded-full bg-primary">
                {loading ? 'Creating...' : 'Create Coupon'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="rounded-full">
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Coupons Table */}
      <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-surface/50">
                <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">Code</th>
                <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">Discount</th>
                <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">Min Order</th>
                <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">Max Discount</th>
                <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">Usage</th>
                <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">Status</th>
                <th className="text-right py-4 px-4 font-body font-medium text-muted-foreground text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((coupon) => (
                <tr key={coupon.coupon_id} className="border-b border-gray-50 hover:bg-surface/30">
                  <td className="py-4 px-4">
                    <span className="font-body font-semibold text-primary">{coupon.code}</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="font-body text-secondary">{coupon.discount_percent}%</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="font-body text-muted-foreground">{formatPrice(coupon.min_order)}</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="font-body text-muted-foreground">{formatPrice(coupon.max_discount)}</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="font-body text-muted-foreground">
                      {coupon.usage_count || 0} / {coupon.usage_limit || '∞'}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <Badge className={isExpired(coupon.expiry_date) ? 'bg-destructive/10 text-destructive' : 'bg-success/10 text-success'}>
                      {isExpired(coupon.expiry_date) ? 'Expired' : 'Active'}
                    </Badge>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex justify-end">
                      <button
                        onClick={() => handleDelete(coupon.coupon_id)}
                        className="p-2 hover:bg-destructive/10 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {coupons.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground font-body">No coupons created yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
